import React, { useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import PasswordStrengthMeter from '../../Components/PasswordStrengthMeter';
import { CognitoUserAttribute } from 'amazon-cognito-identity-js';
import { useSelector } from 'react-redux';
import { RootState } from '../../globalStore/store';
import userPool from '../../util/cognito/userPool';
import { useNotify } from '../../util/hooks/notify/Notification';

interface RegistrationStep4Props {
  handleNextStep: () => void;
}

const RegistrationStep4: React.FC<RegistrationStep4Props> = ({ handleNextStep }) => {
  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isStrongPassword, setIsStrongPassword] = useState<boolean>(false);
  const [passwordError, setPasswordError] = useState<string>('');
  const [checkError, setCheckError] = useState<string>('');
  const [checked, setChecked] = useState<boolean>(false);
  const firstName: string = useSelector((state: RootState) => state.user.firstName);
  const lastName: string = useSelector((state: RootState) => state.user.lastName);
  const email: string = useSelector((state: RootState) => state.user.userName);
  const phone: string = useSelector((state: RootState) => state.user.phone);
  const PIN: string = useSelector((state: RootState) => state.user.PIN);
  const helloPayPin: string = useSelector((state: RootState) => state.user.helloPayPin);
  const notify = useNotify();

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  const handleNext = () => {
    let valid = true;
    if (!isStrongPassword) {
      setPasswordError('A jelszó nem megfelelő');
      valid = false;
    } else {
      setPasswordError('');
    }

    if (!checked) {
      setCheckError('A regisztrációhoz el kell fogadnia az Adatkezelési tájékoztatóban foglaltakat.');
      valid = false;
    } else {
      setCheckError('');
    }

    if (valid) {
      const attributes = [
        new CognitoUserAttribute({ Name: 'given_name', Value: firstName }),
        new CognitoUserAttribute({ Name: 'family_name', Value: lastName }),
        new CognitoUserAttribute({ Name: 'email', Value: email }),
        new CognitoUserAttribute({ Name: 'phone_number', Value: phone }),
        new CognitoUserAttribute({ Name: 'custom:pin', Value: PIN }),
        new CognitoUserAttribute({ Name: 'custom:hellopay_pos_user_id', Value: helloPayPin }),
        new CognitoUserAttribute({ Name: 'custom:termsAndConditions', Value: checked.toString() }),
        new CognitoUserAttribute({ Name: 'custom:privacyPolicy', Value: checked.toString() }),
      ];

      userPool.signUp(email, password, attributes, [], (err, data) => {
        if (err) {
          if (err.message.includes('Password cannot be empty')) {
            notify('A jelszó nem lehet üres!', 'error');
          } else if (err.message.includes('Password did not conform with policy')) {
            notify('Nem felel meg a jelszó-házirendnek!', 'error');
          } else if (err.message.includes('An account with the given email already exists.')) {
            notify('Már létezik egy fiók az adott e-mail címmel!', 'error');
          } else {
            notify('Ismeretlen hiba történt a regisztráció során!', 'error');
            console.error('Registration error:', err);
          }
        } else {
          notify('Sikeres regisztráció!', 'success' );
          console.log('User registered successfully:', data);
          handleNextStep();
        }
      });
    }
  };


  return (
    <>
      <Typography variant={'body2'}>{"Hozz létre egy erős jelszót, amely védi a fiókodat és megfelel a követelményeknek. "}</Typography>
      <Box sx={{ width: '100%' }}>
        <TextField
          fullWidth
          name={'password'}
          type={showPassword ? 'text' : 'password'}
          label={'Jelszó'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={!!passwordError}
          helperText={passwordError}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleTogglePassword}
                  edge="end"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <PasswordStrengthMeter password={password} setIsStrongPassword={setIsStrongPassword} />
      </Box>
      <Typography variant={"body2"}>{"A jelszó legyen legalább 8 karakter hosszúságú, kell tartalmaznia kisbetűt, nagybetűt, továbbá számot és speciális karaktert. Nem lehet benne ékezetes betű és szóköz."}</Typography>
      <Box display="flex" alignItems="center">
        <FormControlLabel
          control={
            <Checkbox
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              name="checkedB"
              color="primary"
            />}
          label={
            <Typography variant="body2">
              Hozzájárulok, hogy személyes adataimat a PinkPOS- fiók regisztrációjára vonatkozó Adatkezelési tájékoztatóban foglaltak szerint kezeljék, amelyet előzetesen megismertem és elfogadok.
            </Typography>
          }
        />
      </Box>
      {checkError && <Typography color="error">{checkError}</Typography>}
      <Button variant={'contained'} color={'primary'} onClick={handleNext}>{'Regisztráció véglegesítése'}</Button>
    </>
  );
};

export default RegistrationStep4;